const STORAGE_KEY = 'cm_tts_enabled_v1';
const STORAGE_VOICE_KEY_PREFIX = 'cm_tts_voice_name_v2_';

const VOICE_PRESETS = {
  en: 'Google UK English Male',
  ro: 'Ioana'
};

export function isTTSSupported() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
}

export function getAvailableVoices() {
  if (!isTTSSupported()) return [];
  try {
    const voices = window.speechSynthesis.getVoices ? window.speechSynthesis.getVoices() : [];
    return Array.isArray(voices) ? voices : [];
  } catch {
    return [];
  }
}

export function getTTSEnabledDefault() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return true;
    return raw === 'true';
  } catch {
    return true;
  }
}

export function setTTSEnabled(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // ignore
  }
}

export function getPreferredVoiceName(lang) {
  try {
    const key = `${STORAGE_VOICE_KEY_PREFIX}${lang || 'en'}`;
    const stored = localStorage.getItem(key) || '';
    if (stored) return stored;
    // Default presets (no dropdown): EN male, RO Ioana.
    return VOICE_PRESETS[lang] || '';
  } catch {
    return VOICE_PRESETS[lang] || '';
  }
}

export function setPreferredVoiceName(lang, name) {
  try {
    const key = `${STORAGE_VOICE_KEY_PREFIX}${lang || 'en'}`;
    localStorage.setItem(key, String(name || ''));
  } catch {
    // ignore
  }
}

export function stopTTS() {
  if (!isTTSSupported()) return;
  window.speechSynthesis.cancel();
}

function normalizeName(s) {
  return String(s || '').trim().toLowerCase();
}

function findVoiceByName(voices, name) {
  if (!name) return null;
  const n = normalizeName(name);
  return voices.find((v) => normalizeName(v.name) === n) || null;
}

export function getPresetVoiceName(lang, voices) {
  const desired = VOICE_PRESETS[lang] || '';
  if (!desired) return '';
  const v = findVoiceByName(voices, desired);
  return v ? v.name : '';
}

function pickVoice({ voiceName, lang }) {
  const voices = getAvailableVoices();
  if (!voices.length) return null;

  const exact = findVoiceByName(voices, voiceName);
  if (exact) return exact;

  // Fallback: try preset for language.
  const presetName = getPresetVoiceName(lang, voices);
  const preset = findVoiceByName(voices, presetName);
  if (preset) return preset;

  // Final fallback: any voice for that language, else first voice.
  const desired = String(lang || 'en').toLowerCase();
  const anyLang = voices.find((v) => String(v.lang || '').toLowerCase().startsWith(desired));
  return anyLang || voices[0];
}

export function speakText({ text, lang, rate = 0.92, pitch = 0.9, voiceName = '' }) {
  if (!isTTSSupported()) return false;
  if (!text || !String(text).trim()) return false;

  window.speechSynthesis.cancel();

  const utter = new SpeechSynthesisUtterance(String(text));
  utter.rate = rate;
  utter.pitch = pitch;

  const voiceNow = pickVoice({ voiceName, lang });
  if (voiceNow) {
    utter.voice = voiceNow;
    // Keep lang aligned with chosen voice so browsers don't override the voice selection.
    utter.lang = voiceNow.lang || (lang === 'ro' ? 'ro-RO' : 'en-US');
  } else {
    utter.lang = lang === 'ro' ? 'ro-RO' : 'en-US';
  }

  window.speechSynthesis.speak(utter);

  if (!voiceNow && window.speechSynthesis.onvoiceschanged) {
    const handler = () => {
      try {
        const voiceLater = pickVoice({ voiceName });
        if (voiceLater) {
          utter.voice = voiceLater;
          utter.lang = voiceLater.lang || utter.lang;
        }
      } finally {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
    window.speechSynthesis.onvoiceschanged = handler;
  }

  return true;
}
